import { useState, useCallback } from 'react';
import axios from 'axios';

/**
 * useChat hook - wraps OpenAI chat completions via direct axios call
 * @param {string} provider - AI provider (currently only 'OPEN_AI' supported)
 * @param {string} model - OpenAI model name (e.g. 'gpt-4o')
 * @param {boolean} stream - streaming flag (not implemented, kept for API compat)
 * @returns {{ response: string|null, isLoading: boolean, error: Error|null, sendMessage: Function }}
 */
export function useChat(provider = 'OPEN_AI', model = 'gpt-4o', stream = false) {
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(
    async (messages, options = {}) => {
      if (!messages || messages?.length === 0) return;

      setIsLoading(true);
      setError(null);
      setResponse(null);

      try {
        const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
        if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
          throw new Error('OpenAI API key not configured');
        }

        const { data } = await axios?.post(
          'https://api.openai.com/v1/chat/completions',
          {
            model,
            messages,
            ...options,
          },
          {
            headers: {
              'Authorization': `Bearer ${apiKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const content = data?.choices?.[0]?.message?.content ?? '';
        setResponse(content);
        return content;
      } catch (err) {
        console.error('[useChat] OpenAI error:', err);
        const message =
          err?.response?.data?.error?.message ||
          err?.message ||
          'Unknown error';
        const errorObj = new Error(message);
        setError(errorObj);
      } finally {
        setIsLoading(false);
      }
    },
    [model]
  );

  return { response, isLoading, error, sendMessage };
}
